import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';

type ModalState = 'form' | 'pending' | 'confirmed';

@Component({
  selector: 'app-change-credentials',
  templateUrl: './change-credentials.component.html',
})
export class ChangeCredentialsComponent implements OnInit, OnDestroy {
  @Input() team_member: any;

  state: ModalState = 'form';
  new_email = '';
  new_username = '';
  current_password = '';
  sending = false;
  request_guid = '';
  sent_to_email = '';
  private poll_interval: any;

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    public globalService: GlobalService
  ) {}

  ngOnInit() {
    this.new_email = this.team_member?.email || '';
    this.new_username = this.team_member?.username || '';
  }

  ngOnDestroy() {
    this.StopPolling();
  }

  get currentUser(): any {
    return this.globalService.GetCurrentUser();
  }

  get isSelf(): boolean {
    return this.currentUser?.guid === this.team_member?.guid;
  }

  get isCurrentUserAdmin(): boolean {
    const v = this.currentUser?.is_admin;
    return v === true || v === 1 || v === '1';
  }

  get isTargetAdmin(): boolean {
    const v = this.team_member?.is_admin;
    return v === true || v === 1 || v === '1';
  }

  get canAccessForm(): boolean {
    if (!this.isCurrentUserAdmin) return false;
    if (this.isTargetAdmin && !this.isSelf) return false;
    return true;
  }

  get blockedReason(): string {
    if (!this.isCurrentUserAdmin) return 'Only admins can change team member credentials.';
    if (this.isTargetAdmin && !this.isSelf) return 'Admin credentials can only be changed by the account owner.';
    return '';
  }

  get canSubmit(): boolean {
    const emailChanged = this.new_email.trim() !== (this.team_member?.email || '').trim();
    const usernameChanged = this.new_username.trim() !== (this.team_member?.username || '').trim();
    if (!emailChanged && !usernameChanged) return false;
    if (this.isSelf && !this.current_password.trim()) return false;
    return !this.sending;
  }

  async Submit() {
    if (!this.canSubmit) return;
    this.sending = true;

    // Only send fields that have actually changed
    const email_to_send = this.new_email.trim() !== (this.team_member?.email || '').trim() ? this.new_email.trim() : '';
    const username_to_send = this.new_username.trim() !== (this.team_member?.username || '').trim() ? this.new_username.trim() : '';

    try {
      const payload: any = { new_email: email_to_send, new_username: username_to_send };
      if (this.isSelf) payload.current_password = this.current_password.trim();
      const data: any = await this.apiService.post(
        `team_members/${this.team_member.guid}/credentials/request`,
        payload
      );
      if (!data.success) {
        this.globalService.presentToastMessage(data.message, false);
        return;
      }
      this.request_guid = data.data[0].request_guid;
      this.sent_to_email = email_to_send || this.team_member.email;
      this.state = 'pending';
      this.StartPolling();
    } catch {
      this.globalService.presentToastMessage('Failed to send confirmation email.', false);
    } finally {
      this.sending = false;
    }
  }

  async Resend() {
    this.StopPolling();
    this.state = 'form';
    await this.Submit();
  }

  Cancel() {
    this.StopPolling();
    this.state = 'form';
  }

  async Close() {
    this.StopPolling();
    this.modalController.dismiss(this.state === 'confirmed');
  }

  private StartPolling() {
    this.poll_interval = setInterval(() => this.CheckStatus(), 3000);
  }

  private StopPolling() {
    if (this.poll_interval) {
      clearInterval(this.poll_interval);
      this.poll_interval = null;
    }
  }

  private async CheckStatus() {
    try {
      const data: any = await this.apiService.get(`team_members/credentials/status/${this.request_guid}`);
      if (!data.success || !data.data?.length) return;
      const record = data.data[0];
      if (record.confirmed) {
        this.StopPolling();
        this.state = 'confirmed';
      } else if (record.expired) {
        this.StopPolling();
        this.globalService.presentToastMessage('The confirmation link has expired. Please resend.', false);
        this.state = 'form';
      }
    } catch {}
  }
}
